import { createSlice } from '@reduxjs/toolkit';
import { TYPE_OF_ANT_DESIGN, SortDirection } from 'common/constant';
import showMessage from 'components/Message';
import i18n from 'i18n/i18n';

interface CalendarData {
  data: {
    hblDatas: any[];
    ccrDatas: any[];
    ownCharge: string[];
  };
  total: number;
}

type CustomsClearanceRequestState = {
  showSearchModal: boolean;
  showCalendarModal: boolean;
  filterSearchData: any;
  filterSearchTotal: number;
  custClearReqDetail: object;
  custClearReqDetailTotal: number;
  pic: object;
  cnee: object;
  holiday: object;
  loading: boolean;
  message: string;
  messageSuccess: string;
  success: boolean;
  showSortModal: boolean;
  sortDirection: string;
  sortSelected: {
    name: string;
    value: string;
  } | null;
  custNo: string;
  filterSearchCalendarData: CalendarData;
  filterSearchCalendarTotal: number;
};

const initialState: CustomsClearanceRequestState = {
  showSearchModal: true,
  showCalendarModal: false,
  filterSearchData: [],
  filterSearchTotal: 0,
  custClearReqDetail: {},
  custClearReqDetailTotal: 0,
  pic: {},
  cnee: {},
  holiday: {},
  loading: false,
  message: '',
  messageSuccess: '',
  success: false,
  showSortModal: false,
  sortDirection: '',
  sortSelected: null,
  custNo: '',
  filterSearchCalendarData: {
    data: {
      hblDatas: [],
      ccrDatas: [],
      ownCharge: [],
    },
    total: 0,
  },
  filterSearchCalendarTotal: 0,
};

export const customsClearanceRequestSlice = createSlice({
  name: 'customsClearanceRequestExport',
  initialState,
  reducers: {
    toggleSearchModal: (state) => {
      state.showSearchModal = !state.showSearchModal;
    },
    toggleCalendarModal: (state) => {
      state.showCalendarModal = !state.showCalendarModal;
    },
    filterCustomsClearanceRequest: (state, action) => {
      state.loading = true;
    },
    filterCustomsClearanceRequestSuccess: (state, action) => {
      if (!action.payload.data?.length) {
        showMessage(TYPE_OF_ANT_DESIGN.WARNING, i18n.t('message.common.0001'));
      } else {
        state.showSearchModal = false;
        state.showCalendarModal = false;
      }
      state.filterSearchData = action.payload.data || [];
      state.filterSearchTotal = action.payload.total;
      state.loading = false;
      state.showSortModal = false;
    },
    filterCustomsClearanceRequestFailure: (state) => {
      state.loading = false;
    },
    getDetailCustClearReq: (state, action) => {},
    getDetailCustClearReqSuccess: (state, action) => {
      state.custClearReqDetail = action.payload.data;
      state.custClearReqDetailTotal = action.payload.total;
    },
    getPic: (state, action) => {},
    getPicSuccess: (state, action) => {
      state.pic = action.payload.data;
    },
    getPicFailure: (state) => {},
    getCnee: (state, action) => {},
    getCneeSuccess: (state, action) => {
      state.cnee = action.payload.data;
    },
    getCneeFailure: (state) => {},
    getShippingInstruction: (state, action) => {},
    getShippingInstructionSuccess: (state, action) => {
      state.cnee = action.payload.data;
    },
    getShippingInstructionFailure: (state) => {},
    registerCustomsClearanceRequest: (state, action) => {
      state.loading = true;
      state.success = false;
    },
    registerCustomsClearanceRequestSuccess: (state, action) => {
      state.loading = false;
      state.success = true;
      state.custNo = action.payload?.data.CUST_CLEAR_REQ_NO;
      state.messageSuccess = action.payload?.message;
    },
    registerCustomsClearanceRequestFailure: (state, action) => {
      state.loading = false;
      state.success = false;
      state.message = action.payload?.message;
    },
    updateCustomsClearanceRequest: (state, action) => {
      state.loading = true;
      state.success = false;
    },
    updateCustomsClearanceRequestSuccess: (state, action) => {
      state.loading = false;
      state.success = true;
      state.messageSuccess = action.payload?.message;
    },
    updateCustomsClearanceRequestFailure: (state, action) => {
      state.loading = false;
      state.success = false;
      state.message = action.payload?.message;
    },
    deleteCustomsClearanceRequest: (state, action) => {
      state.loading = true;
      state.success = false;
    },
    deleteCustomsClearanceRequestSuccess: (state, action) => {
      state.loading = false;
      state.success = true;
      state.messageSuccess = action.payload?.message;
    },
    deleteCustomsClearanceRequestFailure: (state, action) => {
      state.loading = false;
      state.success = false;
      state.message = action.payload.message;
    },
    getHoliday: (state) => {},
    getHolidaySuccess: (state, action) => {
      state.holiday = action.payload.data;
    },
    getHolidayFailure: (state) => {},
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    toggleSortModal: (state) => {
      if (!state.showSortModal) {
        state.showSearchModal = false;
      }
      state.showSortModal = !state.showSortModal;
    },
    setSortSelected: (state, action) => {
      state.sortSelected = action.payload;
      if (action.payload) {
        state.sortDirection = SortDirection.ASC;
      }
      if (!state.filterSearchData.length) {
        state.showSortModal = false;
        state.showSearchModal = true;
      }
    },
    clearSearchData: (state) => {
      state.showSearchModal = true;
      state.showCalendarModal = false;
      state.filterSearchData = [];
      state.filterSearchTotal = 0;
      state.custClearReqDetail = {};
      state.custClearReqDetailTotal = 0;
      state.pic = {};
      state.holiday = {};
      state.loading = false;
      state.message = '';
      state.messageSuccess = '';
      state.success = false;
    },
    uploadOutsideDoc: (state, action) => {},
    uploadOutsideDocSuccess: (state, action) => {
      state.loading = false;
      state.success = true;
      state.custNo = action.payload?.data.CUST_CLEAR_REQ_NO;
      state.messageSuccess = action.payload?.message;
    },
    uploadOutsideDocFailure: (state, action) => {
      state.loading = false;
      state.success = false;
      state.message = action.payload?.message;
    },
    filterCustomsClearanceRequestCalendar: (state, action) => {
      state.loading = true;
    },
    filterCustomsClearanceRequestCalendarSuccess: (state, action) => {
      if (!action.payload.data?.ccrDatas?.length && !action.payload.data?.hblDatas?.length) {
        showMessage(TYPE_OF_ANT_DESIGN.WARNING, i18n.t('message.common.0001'));
      }
      state.filterSearchCalendarData = action.payload.data || {};
      state.filterSearchCalendarTotal = action.payload.total;
      state.loading = false;
    },
    filterCustomsClearanceRequestCalendarFailure: (state) => {
      state.loading = false;
    },
    foldFlagHblDisplayCalendarFlg: (state, action) => {
      state.loading = true;
    },
    foldFlagHblDisplayCalendarFlgSuccess: (state, action) => {
      showMessage(TYPE_OF_ANT_DESIGN.SUCCESS, i18n.t('message.customs_clearance_request.0010'));
      state.loading = false;
    },
    foldFlagHblDisplayCalendarFlgFailure: (state) => {
      state.loading = false;
    },
    clearCalendarData: (state) => {
      state.filterSearchCalendarData = initialState.filterSearchCalendarData;
    },
  },
});

export const {
  toggleSearchModal,
  toggleCalendarModal,
  filterCustomsClearanceRequest,
  filterCustomsClearanceRequestSuccess,
  filterCustomsClearanceRequestFailure,
  getDetailCustClearReq,
  getDetailCustClearReqSuccess,
  getPic,
  getPicSuccess,
  getPicFailure,
  getCnee,
  getCneeSuccess,
  getCneeFailure,
  getShippingInstruction,
  getShippingInstructionSuccess,
  getShippingInstructionFailure,
  registerCustomsClearanceRequest,
  registerCustomsClearanceRequestSuccess,
  registerCustomsClearanceRequestFailure,
  updateCustomsClearanceRequest,
  updateCustomsClearanceRequestSuccess,
  updateCustomsClearanceRequestFailure,
  deleteCustomsClearanceRequest,
  deleteCustomsClearanceRequestSuccess,
  deleteCustomsClearanceRequestFailure,
  getHoliday,
  getHolidaySuccess,
  getHolidayFailure,
  setLoading,
  setSortDirection,
  toggleSortModal,
  setSortSelected,
  clearSearchData,
  uploadOutsideDoc,
  uploadOutsideDocSuccess,
  uploadOutsideDocFailure,
  filterCustomsClearanceRequestCalendar,
  filterCustomsClearanceRequestCalendarSuccess,
  filterCustomsClearanceRequestCalendarFailure,
  foldFlagHblDisplayCalendarFlg,
  foldFlagHblDisplayCalendarFlgSuccess,
  foldFlagHblDisplayCalendarFlgFailure,
  clearCalendarData,
} = customsClearanceRequestSlice.actions;

export default customsClearanceRequestSlice.reducer;
